/**
 * What a column can be filtered to, derived from the rows on screen
 * (design: "Led AI Console", the ISSUES table's column menus).
 *
 * ## The options come from the DATA, never from a list somebody maintains
 *
 * A hard-coded company list goes stale the day a new sub is added, and a
 * hard-coded location list was never right for more than one project. Every
 * option here is a value some row actually has, so the menu cannot offer a
 * filter that matches nothing, and cannot omit one that would.
 *
 * ## Counted against the OTHER filters, not against everything
 *
 * A count beside an option promises "this many rows if you pick me". That is
 * only true if the pool it counts is the rows surviving every filter EXCEPT
 * this column's, which is what `except` is for. Counting against the whole
 * table would promise 40 and deliver 3 the moment a second filter was on, and
 * a number that lies is worse than no number.
 *
 * ## The dot is the worst age in the bucket
 *
 * The design colours each option by the oldest row behind it, so "which
 * company is sitting on the old work" is answerable without opening anything.
 * Thresholds are the artboard's: over 60 days red, over 30 amber, over 7 a
 * mid grey, anything fresher the palest.
 */

import type { ConsoleRow } from "../api";

export interface ColumnFilterOption {
  /** The stored value, and what goes in the URL. `null` is the "all" row. */
  value: string | null;
  label: string;
  /** How many rows this option would leave. */
  count: number;
  /** The oldest row behind the option, in days. */
  worstDays: number;
  selected: boolean;
}

/** Which columns carry a filter menu, and what their two special rows say. */
export interface ColumnFilterDef {
  /** URL parameter, and the `ColumnSort` key. */
  key: string;
  /** The "no filter" row. */
  allLabel: string;
  /** The row for records that have no value at all. */
  noneLabel: string;
  /** Read the filterable value off a row. `null` when it has none. */
  valueOf: (row: ConsoleRow) => string | null;
  /**
   * Order the options by COUNT rather than by the oldest row behind them.
   *
   * Right for a closed vocabulary such as priority or due, where the reader
   * is looking for a named bucket and the list should not reshuffle as
   * records age. Wrong for companies and people, where "who is sitting on the
   * oldest work" is the question the menu is being opened to answer.
   */
  byCount?: boolean;
}

/** The sentinel for "this record has no value in this column". No field
 *  produces a string shaped like this, so it cannot collide with a value. */
export const NO_VALUE = "__none__";

/** The artboard's four age tones, as names the caller resolves to tokens. */
export type WorstTone = "red" | "amber" | "mid" | "pale";

export function worstTone(days: number): WorstTone {
  if (days > 60) return "red";
  if (days > 30) return "amber";
  if (days > 7) return "mid";
  return "pale";
}

/**
 * Does a row survive every active filter EXCEPT one column's?
 *
 * `except` is the column whose own menu is being built. Pass `null` to test
 * against all of them, which is what the table does.
 */
export function passesFilters(
  row: ConsoleRow,
  defs: ColumnFilterDef[],
  active: Record<string, string | null>,
  except: string | null,
): boolean {
  return defs.every((def) => {
    if (def.key === except) return true;
    const wanted = active[def.key];
    if (wanted == null) return true;
    const has = def.valueOf(row);
    return wanted === NO_VALUE ? has === null : has === wanted;
  });
}

/** Rows the table should show: everything surviving every active filter. */
export function applyColumnFilters(
  rows: ConsoleRow[],
  defs: ColumnFilterDef[],
  active: Record<string, string | null>,
): ConsoleRow[] {
  if (defs.every((def) => active[def.key] == null)) return rows;
  return rows.filter((row) => passesFilters(row, defs, active, null));
}

/**
 * The menu for one column.
 *
 * The "all" row leads and carries the whole pool's count, then one row per
 * distinct value. Ordering is `byCount` or worst-age-first, with the count
 * and then the label breaking ties, so the list is stable between renders
 * rather than shuffling on every re-fold.
 */
export function columnFilterOptions(
  rows: ConsoleRow[],
  defs: ColumnFilterDef[],
  active: Record<string, string | null>,
  def: ColumnFilterDef,
  ageOf: (row: ConsoleRow) => number,
): ColumnFilterOption[] {
  const pool = rows.filter((row) => passesFilters(row, defs, active, def.key));
  const buckets = new Map<string, { label: string; count: number; worstDays: number }>();
  let worstOverall = 0;
  for (const row of pool) {
    const raw = def.valueOf(row);
    const key = raw ?? NO_VALUE;
    const days = ageOf(row);
    if (days > worstOverall) worstOverall = days;
    const bucket = buckets.get(key) ?? { label: raw ?? def.noneLabel, count: 0, worstDays: 0 };
    bucket.count += 1;
    if (days > bucket.worstDays) bucket.worstDays = days;
    buckets.set(key, bucket);
  }

  const current = active[def.key] ?? null;
  const rest = [...buckets.entries()]
    .sort(([, left], [, right]) =>
      def.byCount
        ? right.count - left.count || left.label.localeCompare(right.label)
        : right.worstDays - left.worstDays ||
          right.count - left.count ||
          left.label.localeCompare(right.label),
    )
    .map(([key, bucket]) => ({
      value: key,
      label: bucket.label,
      count: bucket.count,
      worstDays: bucket.worstDays,
      selected: current === key,
    }));

  return [
    {
      value: null,
      label: def.allLabel,
      count: pool.length,
      worstDays: worstOverall,
      selected: current === null,
    },
    ...rest,
  ];
}

/** What the header chip reads: the chosen option's label, or the column's own
 *  name while nothing is chosen. */
export function columnFilterLabel(options: ColumnFilterOption[], fallback: string): string {
  const chosen = options.find((option) => option.selected && option.value !== null);
  return chosen ? chosen.label : fallback;
}

// ---------------------------------------------------------------------------
// The URL
// ---------------------------------------------------------------------------

/**
 * Column filters live in the query, for the reason `columnSort` gives: a
 * narrowed table is a link somebody sends. One parameter per column, prefixed
 * so a future `?company=` meaning something else cannot collide with it.
 */
const PREFIX = "f.";

export function columnFilterParam(column: string): string {
  return `${PREFIX}${column}`;
}

/** Every column filter in force. Columns with nothing chosen are absent. */
export function readColumnFilters(
  search: string,
  defs: ColumnFilterDef[],
): Record<string, string | null> {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const active: Record<string, string | null> = {};
  for (const def of defs) {
    const raw = params.get(columnFilterParam(def.key));
    if (raw) active[def.key] = raw;
  }
  return active;
}

/** Whether anything is narrowing the table — what a "Clear filters" control
 *  keys off. */
export function anyColumnFilter(active: Record<string, string | null>): boolean {
  return Object.values(active).some((value) => value != null);
}

/** Clearing every column at once, as `[key, null]` pairs for one navigation.
 *  Three sequential single-parameter navigations would each build from the
 *  same stale search and only the last would survive. */
export function clearColumnFilterParams(
  defs: ColumnFilterDef[],
): Array<[string, string | null]> {
  return defs.map((def) => [columnFilterParam(def.key), null]);
}
